import * as React from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"

export interface TagInputProps {
  /** Current tags as comma-separated string or array */
  value: string | string[]
  /** Callback when tags change - receives comma-separated string */
  onChange: (value: string) => void
  /** Placeholder text for the input */
  placeholder?: string
  /** Whether the input is disabled */
  disabled?: boolean
  /** Additional class names */
  className?: string
  /** Badge variant for tags */
  variant?: "default" | "secondary" | "outline" | "info" | "success"
  /** ID for the input element */
  id?: string
}

/**
 * A reusable tag input component that displays tags as badges.
 * Tags can be added by typing and pressing Enter or comma.
 * Tags can be removed by clicking the X button or pressing Backspace.
 */
const TagInput = React.forwardRef<HTMLDivElement, TagInputProps>(
  (
    {
      value,
      onChange,
      placeholder = "Type and press Enter to add",
      disabled = false,
      className,
      variant = "secondary",
      id,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState("")
    const inputRef = React.useRef<HTMLInputElement>(null)

    // Parse value to array of tags
    const tags = React.useMemo(() => {
      if (Array.isArray(value)) {
        return value.filter((tag) => tag.trim() !== "")
      }
      if (typeof value === "string" && value.trim()) {
        return value
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag !== "")
      }
      return []
    }, [value])

    // Convert tags array back to comma-separated string
    const updateTags = (newTags: string[]) => {
      onChange(newTags.join(", "))
    }

    const addTag = (tag: string) => {
      const trimmedTag = tag.trim()
      if (trimmedTag && !tags.includes(trimmedTag)) {
        updateTags([...tags, trimmedTag])
      }
      setInputValue("")
    }

    const removeTag = (indexToRemove: number) => {
      updateTags(tags.filter((_, index) => index !== indexToRemove))
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault()
        if (inputValue.trim()) {
          addTag(inputValue)
        }
      } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
        removeTag(tags.length - 1)
      }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      // If user types a comma, add the tag before the comma
      if (newValue.includes(",")) {
        const parts = newValue.split(",")
        const tagToAdd = parts[0].trim()
        if (tagToAdd) {
          addTag(tagToAdd)
        }
        // Keep any text after the last comma
        setInputValue(parts.slice(1).join(",").trimStart())
      } else {
        setInputValue(newValue)
      }
    }

    const handleContainerClick = () => {
      inputRef.current?.focus()
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-3 py-1.5 text-base shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
        onClick={handleContainerClick}
      >
        {tags.map((tag, index) => (
          <Badge
            key={`${tag}-${index}`}
            variant={variant}
            className="flex items-center gap-1 pr-1"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  removeTag(index)
                }}
                className="ml-1 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                aria-label={`Remove ${tag}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? placeholder : ""}
          disabled={disabled}
          className={cn(
            "flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed",
            "min-w-[120px] text-sm"
          )}
        />
      </div>
    )
  }
)

TagInput.displayName = "TagInput"

export { TagInput }
